import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/newsletter/subscribe';
const HTTP_METHOD = 'post';

export type MakeSubscribeNewsletterDeps<NewsletterSubscribeInputSchema extends Schema.Top> = {
  readonly newsletterSubscribeInput: NewsletterSubscribeInputSchema;
};

export const makeSubscribeNewsletter = <NewsletterSubscribeInputSchema extends Schema.Top>(
  deps: MakeSubscribeNewsletterDeps<NewsletterSubscribeInputSchema>
) => {
  const SubscribeNewsletterError = createBackendApiError({
    domain: 'Newsletter',
    endpoint: 'Subscribe',
    path: ENDPOINT,
    identifierField: 'email',
  });

  const postNewsletterSubscriptionFetch = (email: string) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/json', 'application/json')
        .fetch({
          body: {
            email,
            name: null,
            surname: null,
            company: null,
            city: null,
            street: null,
            zip: null,
            country: null,
            phone: null,
          },
        });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'newsletter.email': email,
        },
      })
    );

  const subscribeNewsletter = (email: string) =>
    postNewsletterSubscriptionFetch(email).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.newsletterSubscribeInput)(response),
          404: (response) =>
            Effect.fail(new SubscribeNewsletterError.NotFoundError({ email, response })),
          422: SubscribeNewsletterError.createViolationError(email),
          orElse: (response) =>
            Effect.fail(new SubscribeNewsletterError.ApiRequestError({ email, response })),
        })
      ),
      Effect.tap(() =>
        logApiOperation(
          ApiModule.CustomerNewsletterSubscribe,
          'Debug',
          `Successfully subscribed newsletter for email: ${email}`
        )
      ),
      Effect.withSpan('api.customer.subscribeNewsletter', {
        kind: 'internal',
        attributes: {
          'newsletter.email': email,
        },
      })
    );

  return { SubscribeNewsletterError, subscribeNewsletter };
};
